import { useEffect, useState } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useTournament } from '@/context/TournamentContext';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function OtherPicks() {
  const [user, setUser] = useState(null);
  const [allPicks, setAllPicks] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const { currentTournament, selectedTournamentId } = useTournament();

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTournamentId) return;

      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        try {
          const picksRes = await axios.get(
            `${API}/picks/users?tournament_id=${selectedTournamentId}`
          );
          setAllPicks(picksRes.data);
        } catch (err) {
          // No active tournament or picks
          console.log('No active tournament');
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedTournamentId]);

  const toggleUser = (userId) => {
    setExpandedUsers({
      ...expandedUsers,
      [userId]: !expandedUsers[userId],
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <div className="flex items-center space-x-3">
            <Users size={48} className="text-amber-600" />
            <div>
              <h1 className="font-bebas text-5xl tracking-wider">All Picks</h1>
              <p className="font-manrope text-emerald-100">See what everyone picked</p>
            </div>
          </div>
        </div>

        {!currentTournament && (
          <div className="bg-amber-100 border-l-4 border-amber-600 p-6 mb-8 rounded-md">
            <p className="font-manrope text-amber-900">
              <strong>No active tournament.</strong> Check back soon or contact an admin.
            </p>
          </div>
        )}

        {currentTournament && allPicks.length === 0 && (
          <div className="bg-white rounded-md card-shadow p-8 text-center">
            <p className="font-manrope text-gray-600 text-lg">
              No picks have been submitted yet. Be the first to make your selections!
            </p>
          </div>
        )}

        {currentTournament && allPicks.length > 0 && (
          <div className="space-y-4">
            {allPicks.map((userPick) => (
              <div
                key={userPick.user_id}
                data-testid={`user-picks-${userPick.user_id}`}
                className="bg-white rounded-md card-shadow overflow-hidden"
              >
                <div
                  onClick={() => toggleUser(userPick.user_id)}
                  className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    {userPick.picture && (
                      <img
                        src={userPick.picture}
                        alt={userPick.name}
                        className="w-12 h-12 rounded-full border-2 border-emerald-900"
                      />
                    )}
                    <div>
                      <div className="font-bebas text-2xl text-emerald-900 tracking-wide">
                        {userPick.name}
                        {userPick.user_id === user?.user_id && (
                          <span className="ml-2 text-sm bg-amber-600 text-white px-2 py-1 rounded">
                            YOU
                          </span>
                        )}
                      </div>
                      <div className="font-manrope text-sm text-gray-600">
                        {userPick.picks.length} picks made
                      </div>
                    </div>
                  </div>
                  {expandedUsers[userPick.user_id] ? (
                    <ChevronUp className="text-emerald-900" size={24} />
                  ) : (
                    <ChevronDown className="text-emerald-900" size={24} />
                  )}
                </div>

                {expandedUsers[userPick.user_id] && (
                  <div className="p-6 bg-gray-50 border-t border-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {userPick.picks
                        .sort((a, b) => a.seed - b.seed)
                        .map((pick) => (
                          <div
                            key={pick.pick_id}
                            className="bg-white p-4 rounded-md border border-emerald-900"
                          >
                            <div className="font-bebas text-lg text-amber-600">Seed {pick.seed}</div>
                            <div className="font-manrope text-emerald-900 font-bold">
                              {pick.team_name}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}